// "Standard cost inputs": the figures a parts list carries besides its lines.
// Labor minutes and overhead percent are plain numbers; the hourly rate is
// money, so it shows only to can_financial (the API strips it otherwise) and
// travels as integer cents. Each field saves when it loses focus.

import React, { useEffect, useState } from 'react';
import { laneLabel } from '../components/review/fieldStyles';
import { Field, TextArea } from '../components/ui/Field';
import { Alert } from '../components/ui/Alert';
import Panel from '../components/ui/Panel';
import { centsToDollarsInput, dollarsToCents } from '../Procurement/poApi';
import { bomErrorMessage, formatQty, type BomHeader, type HeaderPatch } from './bomsApi';

interface Props {
  bom: BomHeader;
  canEdit: boolean;
  canFinancial: boolean;
  onSave: (patch: HeaderPatch) => Promise<void>;
}

const numberOrNull = (text: string): number | null | 'bad' => {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : 'bad';
};

const shown = (v: string | null) => (v === null || v === '' ? '' : formatQty(v));

const BomFigures: React.FC<Props> = ({ bom, canEdit, canFinancial, onSave }) => {
  const [minutes, setMinutes] = useState(shown(bom.labor_minutes));
  const [overhead, setOverhead] = useState(shown(bom.overhead_percent));
  const [rate, setRate] = useState(centsToDollarsInput(bom.labor_rate_cents));
  const [notes, setNotes] = useState(bom.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A new revision, or a response that changed a figure, replaces the drafts.
  useEffect(() => {
    setMinutes(shown(bom.labor_minutes));
    setOverhead(shown(bom.overhead_percent));
    setRate(centsToDollarsInput(bom.labor_rate_cents));
    setNotes(bom.notes ?? '');
    setError(null);
  }, [bom.id, bom.labor_minutes, bom.overhead_percent, bom.labor_rate_cents, bom.notes]);

  const save = async (patch: HeaderPatch, revert: () => void) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(patch);
    } catch (err) {
      revert();
      setError(bomErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const commitNumber = (
    text: string,
    current: string | null,
    field: 'labor_minutes' | 'overhead_percent',
    reset: (v: string) => void
  ) => {
    const parsed = numberOrNull(text);
    if (parsed === 'bad') {
      reset(shown(current));
      setError('Enter a number, or leave it empty.');
      return;
    }
    const before = current === null ? null : Number(current);
    if (parsed === before) return;
    save({ [field]: parsed } as HeaderPatch, () => reset(shown(current)));
  };

  const commitRate = () => {
    const before = bom.labor_rate_cents ?? null;
    const t = rate.trim();
    const cents = dollarsToCents(t);
    if (t !== '' && (cents === null || cents < 0)) {
      setRate(centsToDollarsInput(before));
      setError('Enter the hourly rate in dollars, or leave it empty.');
      return;
    }
    if (cents === before) return;
    save({ labor_rate_cents: cents }, () => setRate(centsToDollarsInput(before)));
  };

  const commitNotes = () => {
    const next = notes.trim() === '' ? null : notes;
    if (next === (bom.notes ?? null)) return;
    save({ notes: next }, () => setNotes(bom.notes ?? ''));
  };

  const cls = 'text-right tabular-nums';

  return (
    <Panel
      title="Standard cost inputs"
      actions={saving ? <span className="text-xs text-on-surface-variant">Saving…</span> : undefined}
    >

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="min-w-0">
          <label htmlFor="bom-labor-minutes" className={laneLabel}>
            Labor minutes
          </label>
          <Field
            thumb
            id="bom-labor-minutes"
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            value={minutes}
            readOnly={!canEdit}
            placeholder="0"
            onChange={(e) => setMinutes(e.target.value)}
            onBlur={() => canEdit && commitNumber(minutes, bom.labor_minutes, 'labor_minutes', setMinutes)}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={cls}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="bom-overhead" className={laneLabel}>
            Overhead percent
          </label>
          <Field
            thumb
            id="bom-overhead"
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            value={overhead}
            readOnly={!canEdit}
            placeholder="0"
            onChange={(e) => setOverhead(e.target.value)}
            onBlur={() => canEdit && commitNumber(overhead, bom.overhead_percent, 'overhead_percent', setOverhead)}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={cls}
          />
        </div>
        {canFinancial && (
          <div className="min-w-0">
            <label htmlFor="bom-rate" className={laneLabel}>
              Hourly rate (dollars)
            </label>
            <Field
              thumb
              id="bom-rate"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={rate}
              readOnly={!canEdit}
              placeholder="0.00"
              onChange={(e) => setRate(e.target.value)}
              onBlur={() => canEdit && commitRate()}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              className={cls}
            />
          </div>
        )}
      </div>

      <div className="mt-3">
        <label htmlFor="bom-notes" className={laneLabel}>
          Notes
        </label>
        <TextArea
          id="bom-notes"
          rows={3}
          value={notes}
          readOnly={!canEdit}
          placeholder={canEdit ? 'Anything the next person building this should know' : ''}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => canEdit && commitNotes()}
        />
      </div>

      <Alert show={!!error} variant="text" className="mt-3">
        Not saved: {error}
      </Alert>
    </Panel>
  );
};

export default BomFigures;
